import TokenAdapter from '../../services/TokenAdapter';
import UnifiedDataManager from '../../services/UnifiedDataManager';
import resolveCharacterSheetData from '../CharacterSheetDataResolver';
import { buildCombatActionCatalog } from '../combat/CombatActionCatalog';
import { restoreSnapshotToken } from './BattleRoomSnapshotRehydrator';
import {
  extractSnapshotEntityReference,
  normalizeSnapshotEntityType
} from './BattleRoomSnapshotContract';

const cloneValue = (value) => {
  if (value === undefined) return undefined;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {
    return value;
  }
};

const addReferenceIdentifier = (identifiers, value) => {
  if (value === undefined || value === null) return;
  const normalized = String(value).trim();
  if (normalized) identifiers.add(normalized);
};

/**
 * Resolve an initiative/turn reference back to the one canonical token record.
 * Initiative owns ordering only; it must never become a competing copy of the
 * actor's actions, equipment, conditions, or turn-scoped combat flags.
 *
 * Supports both the VCS token id and the durable GMA combatant id carried by
 * token.combatantId. Returns the object from `tokens` without merging fields
 * from the reference.
 * date_of_change: 2026-07-15
 */
export const selectCanonicalCombatantToken = (tokens, reference) => {
  if (!Array.isArray(tokens) || tokens.length === 0 || reference === undefined || reference === null) {
    return null;
  }

  const identifiers = new Set();
  if (typeof reference === 'object') {
    addReferenceIdentifier(identifiers, reference.id);
    addReferenceIdentifier(identifiers, reference.tokenId);
    addReferenceIdentifier(identifiers, reference.combatantId);
  } else {
    addReferenceIdentifier(identifiers, reference);
  }
  if (identifiers.size === 0) return null;

  // Prefer an exact token id. A combatant id is the linkage fallback used by
  // service-managed rooms whose durable actor ids differ from visual token ids.
  return tokens.find((token) => identifiers.has(String(token?.id)))
    || tokens.find((token) => identifiers.has(String(token?.combatantId)))
    || null;
};

/**
 * Preserve client-owned, turn-scoped mechanics when a service refresh replaces
 * the canonical map snapshot. Durable HP/conditions/positions still come from
 * the service; combatFlags remain local until their owning action resolves or
 * the turn lifecycle clears them.
 * date_of_change: 2026-07-15
 */
export const preserveLocalCombatRuntime = (incomingTokens, currentTokens, options = {}) => (
  (Array.isArray(incomingTokens) ? incomingTokens : []).map((incomingToken) => {
    if (options.combatantReference != null) {
      const activeToken = selectCanonicalCombatantToken(currentTokens, options.combatantReference);
      const incomingIsActive = activeToken && (
        String(incomingToken?.id) === String(activeToken.id)
        || (
          incomingToken?.combatantId != null
          && activeToken.combatantId != null
          && String(incomingToken.combatantId) === String(activeToken.combatantId)
        )
      );
      if (!incomingIsActive) return incomingToken;
    }

    const currentToken = selectCanonicalCombatantToken(currentTokens, incomingToken);
    if (!currentToken || !Object.prototype.hasOwnProperty.call(currentToken, 'combatFlags')) {
      return incomingToken;
    }

    return {
      ...incomingToken,
      combatFlags: {
        ...(incomingToken?.combatFlags && typeof incomingToken.combatFlags === 'object'
          ? cloneValue(incomingToken.combatFlags)
          : {}),
        ...(currentToken?.combatFlags && typeof currentToken.combatFlags === 'object'
          ? cloneValue(currentToken.combatFlags)
          : {})
      }
    };
  })
);

const TURN_START_COMBAT_FLAG_DEFAULTS = Object.freeze({
  actionUsed: false,
  bonusActionUsed: false,
  reactionUsed: false,
  freeObjectUsed: false,
  movementUsed: 0,
  dashUsedThisTurn: false,
  steadyAimActive: false,
  movementLockedThisTurn: false,
  movementLockReason: null,
  disengagedThisTurn: false,
  dodging: false,
  helpTarget: null,
  readiedAction: null,
  sneakAttackUsedRound: null,
  sneakAttackUsedTurn: null,
  cunningStrikeWithdraw: null
});

/**
 * Produce the canonical beginning-of-turn combat flag snapshot while retaining
 * durable/unresolved state such as Hidden observer data. This keeps turn reset
 * mechanics out of initiative projections and prevents an old token snapshot
 * from refunding or retaining only part of the action economy.
 * date_of_change: 2026-07-15
 */
export const resetTurnScopedCombatFlags = (combatFlags = {}) => ({
  ...(combatFlags && typeof combatFlags === 'object' ? cloneValue(combatFlags) : {}),
  ...TURN_START_COMBAT_FLAG_DEFAULTS
});

/**
 * Build the one canonical token patch for a successful drag move. Position and
 * movement expenditure must commit together; otherwise the movement tracker can
 * accept a move while the session snapshot still advertises the old allowance.
 * date_of_change: 2026-07-18
 */
export const buildCanonicalMovementTokenUpdate = (token, position, movementSummary, options = {}) => {
  const x = Number(position?.x);
  const y = Number(position?.y);
  const gridX = Number(position?.grid?.x);
  const gridY = Number(position?.grid?.y);
  const movementUsed = Number(movementSummary?.usedMovement);

  if (![x, y, gridX, gridY, movementUsed].every(Number.isFinite)) {
    return {
      success: false,
      error: 'Movement commit requires finite position, grid, and movement-used values.'
    };
  }

  return {
    success: true,
    props: {
      x,
      y,
      grid: { x: gridX, y: gridY },
      combatFlags: {
        ...(token?.combatFlags && typeof token.combatFlags === 'object'
          ? cloneValue(token.combatFlags)
          : {}),
        movementUsed: Math.max(0, movementUsed),
        ...(options.combatFlags && typeof options.combatFlags === 'object'
          ? cloneValue(options.combatFlags)
          : {})
      }
    }
  };
};

export const selectCombatantEntityPayload = (combatant) => {
  if (!combatant || typeof combatant !== 'object') return null;

  return (
    combatant.entityPayload
    || combatant.characterData
    || combatant.monsterData
    || combatant.schemaData
    || combatant.data
    || combatant.canonicalData
    || combatant.originalUnifiedEntity?.data
    || null
  );
};

export const selectCombatantRuntimeOverlay = (combatant) => (
  combatant?.runtimeOverlay && typeof combatant.runtimeOverlay === 'object'
    ? combatant.runtimeOverlay
    : combatant || null
);

export const selectCombatantVisualState = (combatant) => (
  combatant?.visualState && typeof combatant.visualState === 'object'
    ? combatant.visualState
    : combatant || null
);

const resolveRenderedGridState = (...sources) => {
  for (const source of sources) {
    const gridX = Number(source?.grid?.x);
    const gridY = Number(source?.grid?.y);
    if (Number.isFinite(gridX) && Number.isFinite(gridY)) {
      return { grid: { x: gridX, y: gridY }, gridX, gridY };
    }
  }

  for (const source of sources) {
    const gridX = Number(source?.gridX);
    const gridY = Number(source?.gridY);
    if (Number.isFinite(gridX) && Number.isFinite(gridY)) {
      return { grid: { x: gridX, y: gridY }, gridX, gridY };
    }
  }

  return { grid: undefined, gridX: undefined, gridY: undefined };
};

const hasRichCanonicalSource = (value) => Boolean(
  value?.data
  || value?.schemaData
  || value?.canonicalData
  || value?.originalUnifiedEntity?.data
  || value?.compat?.originalData
);

const hasStructuredCharacterContent = (value) => Boolean(
  hasRichCanonicalSource(value)
  || value?.spells
  || value?.spellcasting
  || value?.equipment
  || value?.features
  || value?.classFeatures
  || value?.items
  || value?.feats
);

const readLocalEntityMap = (entityType) => {
  try {
    const storageKey = UnifiedDataManager?.storageKey || 'ascendant_unified_entities';
    const rawData = localStorage.getItem(storageKey);
    if (!rawData) return {};
    const parsed = JSON.parse(rawData);
    return parsed?.[`${entityType}s`] || {};
  } catch (_) {
    return {};
  }
};

const shouldAttemptCanonicalFallback = (combatant, entityPayload, entityType) => {
  if (!combatant || typeof combatant !== 'object') return false;

  if (entityType === 'monster') {
    return !hasRichCanonicalSource(entityPayload);
  }

  if (hasStructuredCharacterContent(entityPayload)) {
    return false;
  }

  const entityRef = extractSnapshotEntityReference(combatant);
  return Boolean(
    entityRef?.entityId
    || combatant?.baseTemplate
    || combatant?.entity?.id
    || combatant?.originalUnifiedEntity?.id
  );
};

export const selectCombatantProjection = (combatant) => {
  if (!combatant || typeof combatant !== 'object') return null;

  const entityType = normalizeSnapshotEntityType(
    combatant?.type
    || combatant?.entityRef?.entityType
    || combatant?.entity?.type
  );
  const entityPayload = selectCombatantEntityPayload(combatant);
  const canonicalFallbackMap = shouldAttemptCanonicalFallback(combatant, entityPayload, entityType)
    ? readLocalEntityMap(entityType)
    : null;
  const canonicalFallbackToken = canonicalFallbackMap
    ? restoreSnapshotToken({
      snapshotToken: combatant,
      templateMap: {},
      canonicalCharacterEntities: entityType === 'monster' ? {} : canonicalFallbackMap,
      canonicalMonsterEntities: entityType === 'monster' ? canonicalFallbackMap : {}
    }).token
    : combatant;
  const effectiveCombatant = canonicalFallbackToken || combatant;
  const effectiveEntityPayload = selectCombatantEntityPayload(effectiveCombatant) || entityPayload;
  const runtimeOverlay = selectCombatantRuntimeOverlay(combatant);
  const visualState = selectCombatantVisualState(combatant);
  // Visual geometry is authoritative for the rendered token. Runtime overlays
  // own mutable mechanics (HP, initiative, action economy, and similar state),
  // but compact setup placement intentionally updates visualState without
  // rewriting the mechanics overlay. Reading runtimeOverlay geometry first can
  // therefore make range/targeting snap back to a token's pre-placement square
  // as soon as combat begins. date_of_change: 2026-08-26
  const renderedGrid = resolveRenderedGridState(visualState, effectiveCombatant, combatant, runtimeOverlay);
  const tokenLikeEntity = {
    ...(effectiveEntityPayload && typeof effectiveEntityPayload === 'object' ? cloneValue(effectiveEntityPayload) : {}),
    ...cloneValue(effectiveCombatant),
    ...cloneValue(visualState),
    ...cloneValue(runtimeOverlay),
    type: entityType,
    characterData: entityType === 'monster'
      ? undefined
      : (effectiveCombatant.characterData || effectiveEntityPayload || undefined),
    monsterData: entityType === 'monster'
      ? (effectiveCombatant.monsterData || effectiveEntityPayload || undefined)
      : undefined
  };

  const projectedCore = TokenAdapter.toTokenCore(tokenLikeEntity);

  return {
    ...cloneValue(effectiveCombatant),
    ...cloneValue(visualState),
    ...cloneValue(runtimeOverlay),
    ...projectedCore,
    // The root combatant record owns the unique BattleRoom token identity.
    // Nested runtime/visual projections may contain a legacy template ID and
    // must never replace the mutation target. date_of_change: 2026-08-25
    id: combatant?.id || effectiveCombatant?.id || runtimeOverlay?.id || visualState?.id || null,
    layer: visualState?.layer || effectiveCombatant?.layer || combatant?.layer || runtimeOverlay?.layer,
    x: visualState?.x ?? effectiveCombatant?.x ?? combatant?.x ?? runtimeOverlay?.x,
    y: visualState?.y ?? effectiveCombatant?.y ?? combatant?.y ?? runtimeOverlay?.y,
    // Match Token.js and RangeCalculator: nested grid is the canonical rendered position.
    // date_of_change: 2026-06-23
    ...(renderedGrid.grid ? { grid: renderedGrid.grid } : {}),
    gridX: renderedGrid.gridX,
    gridY: renderedGrid.gridY,
    sizeCells: visualState?.sizeCells ?? effectiveCombatant?.sizeCells ?? combatant?.sizeCells ?? runtimeOverlay?.sizeCells,
    width: visualState?.width ?? effectiveCombatant?.width ?? combatant?.width ?? runtimeOverlay?.width,
    height: visualState?.height ?? effectiveCombatant?.height ?? combatant?.height ?? runtimeOverlay?.height,
    entityPayload: effectiveCombatant?.entityPayload || combatant?.entityPayload || effectiveEntityPayload || entityPayload || null,
    runtimeOverlay: combatant?.runtimeOverlay,
    visualState: combatant?.visualState,
    characterData: projectedCore.characterData || effectiveCombatant?.characterData || combatant?.characterData,
    monsterData: projectedCore.monsterData || effectiveCombatant?.monsterData || combatant?.monsterData
  };
};

export const selectCombatantCombatStats = (combatant) => {
  const projection = selectCombatantProjection(combatant);
  if (!projection) return null;

  return {
    hp: projection.hp,
    maxHp: projection.maxHp,
    ac: projection.ac,
    initiative: projection.initiative,
    speed: projection.speed,
    proficiencyBonus: projection.proficiencyBonus,
    abilities: projection.abilities,
    stats: projection.stats
  };
};

export const selectCombatantActions = (combatant) => (
  buildCombatActionCatalog(selectCombatantProjection(combatant))
);

export const selectCombatantSheetData = (combatant) => {
  const projection = selectCombatantProjection(combatant);
  if (!projection) return null;

  if (normalizeSnapshotEntityType(projection.type) === 'monster') {
    return projection;
  }

  return resolveCharacterSheetData(projection);
};

export const selectCombatantEntityReference = (combatant) => (
  extractSnapshotEntityReference(combatant)
);

const BattleRoomCombatantSelectors = {
  selectCombatantEntityPayload,
  selectCanonicalCombatantToken,
  preserveLocalCombatRuntime,
  resetTurnScopedCombatFlags,
  selectCombatantRuntimeOverlay,
  selectCombatantVisualState,
  selectCombatantProjection,
  selectCombatantCombatStats,
  selectCombatantActions,
  selectCombatantSheetData,
  selectCombatantEntityReference
};

export default BattleRoomCombatantSelectors;
